import React, { useCallback, useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  GET_PENDING_JOB_FORM_COUNT,
  GET_PENDING_REFERRAL_COUNT,
  GET_QUOTE_REQUEST_COUNT,
} from "../../../../utils/GraphQL/queries";
import { useSSEListener, SSE_RECONNECTED } from "../../../../utils/SSE/SSEContext";
import QuoteRequestsModal from "../QuoteRequestsModal/QuoteRequestsModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import "./attentionTiles.css";

/**
 * AttentionTiles — the Tasks console's count row.
 *
 * ─────────────────────────────────────────────────────────────────
 * FOUR RENDER STATES, NOT THREE — and the order matters.
 *
 *   error            -> dimmed, "—", title="Couldn't load"
 *   count == null    -> skeleton        (not fetched YET)
 *   Number(c) === 0  -> dimmed + dash   (fetched, nothing to do)
 *   else             -> normal
 *
 * `error` is listed first because Apollo leaves `data` undefined on failure, so
 * `count == null` catches BOTH — without the explicit branch a failing resolver
 * leaves a permanent shimmer on the post-login landing page with no affordance.
 *
 * And the zero/not-fetched split is the whole point: a dash meaning both tells
 * the viewer nothing. `0` before a fetch lands is a lie (the registry's
 * "`count` must not lie" rule), and for Job Forms and Referrals a genuine 0 is
 * the common case, so the two states have to look different.
 *
 * NOTE `totalDoc` IS A STRING. `"0"` is truthy, so `count ? … : …` paints an
 * empty tile as actionable and `!count` never shows the skeleton. Every
 * comparison here goes through `Number()` or `== null` deliberately.
 *
 * ─────────────────────────────────────────────────────────────────
 * COLOUR IS STATUS ONLY. No per-tile identity colour — a tile that is
 * permanently coloured has no way left to say something is wrong with it.
 *
 * QUOTE REQUESTS IS A VOLUME READOUT, not an attention count: it is a 7-day
 * ARRIVAL count, so it reads ~13 whether the office has done everything or
 * nothing. It never takes the attention colour, and the row is headed
 * "At a glance" rather than "Needs attention" because of it.
 *
 * LIVE UPDATES. Job Forms and Referrals ride the SSE events the navbar badge
 * already uses, so they stay in step with it. Quote Requests has NO live
 * trigger — `sendMessage` creates the record and never broadcasts — so it is
 * mount + reconnect only, which `cache-and-network` makes survivable.
 * Every listener callback is `useCallback`-stable: `useSSEListener` lists the
 * callback in its deps, so an inline arrow resubscribes on every render.
 */

const TILE_SKELETON = "skeleton";

const AttentionTiles = ({ tasks, sortLabel }) => {
  const navigate = useNavigate();
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const [jobForms, setJobForms] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [quotes, setQuotes] = useState(null);
  const [failed, setFailed] = useState({});

  const [fetchJobForms] = useLazyQuery(GET_PENDING_JOB_FORM_COUNT, {
    fetchPolicy: "cache-and-network",
  });
  const [fetchReferrals] = useLazyQuery(GET_PENDING_REFERRAL_COUNT, {
    fetchPolicy: "cache-and-network",
  });
  const [fetchQuotes] = useLazyQuery(GET_QUOTE_REQUEST_COUNT, {
    fetchPolicy: "cache-and-network",
  });

  const loadAll = useCallback(() => {
    /*
     * `error` is read off the RESOLVED result, not from a `.catch`.
     * useLazyQuery's execute promise RESOLVES on a GraphQL error — it
     * subscribes with an `error` handler that calls `resolve` — so a
     * `.catch`-only design is unreachable code, and the tile would sit on
     * `count == null` forever: a permanent shimmer with no affordance, which
     * is exactly the failure the four-state design exists to prevent. The
     * `.catch` stays for genuine link/network throws.
     */
    const settle = (setter, pick, key) => ({ data, error }) => {
      if (error) {
        setFailed((p) => ({ ...p, [key]: true }));
        return;
      }
      setFailed((p) => ({ ...p, [key]: false }));
      setter(pick(data) ?? null);
    };
    const boom = (key) => () => setFailed((p) => ({ ...p, [key]: true }));

    fetchJobForms()
      .then(settle(setJobForms, (d) => d?.getPendingJobFormCount, "jobForms"))
      .catch(boom("jobForms"));
    fetchReferrals()
      .then(settle(setReferrals, (d) => d?.getPendingReferralCount, "referrals"))
      .catch(boom("referrals"));
    fetchQuotes()
      .then(settle(setQuotes, (d) => d?.getQuoteRequests?.totalDoc, "quotes"))
      .catch(boom("quotes"));
  }, [fetchJobForms, fetchReferrals, fetchQuotes]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const onJobFormSSE = useCallback((data) => {
    if (data?.pendingJobFormCount != null) setJobForms(data.pendingJobFormCount);
  }, []);
  const onReferralSSE = useCallback((data) => {
    if (data?.pendingReferralCount != null) {
      setReferrals(data.pendingReferralCount);
    }
  }, []);
  /* SSE has no backfill, so anything missed while the socket was down has to be
     re-read here. The navbar gets the same listener so badge and tile
     reconcile together rather than disagreeing after a gap. */
  const onReconnect = useCallback(() => loadAll(), [loadAll]);

  useSSEListener("job_form_update", onJobFormSSE);
  useSSEListener("referral_update", onReferralSSE);
  useSSEListener(SSE_RECONNECTED, onReconnect);

  /* Free: the task array is already in state. Overdue tasks carry the earliest
     due dates and the server sorts ascending before its 80-row limit, so they
     are never the rows truncated — but the count IS capped at 80, hence "80+". */
  const overdueCount =
    tasks === null || tasks === undefined
      ? null
      : tasks.filter((t) => {
          const due = parseInt(t?.dueDate, 10);
          return Number.isFinite(due) && due < Date.now();
        }).length;

  const renderTile = ({ key, label, count, isError, onClick, tone, ariaLabel }) => {
    let body;
    let stateClass;

    if (isError) {
      /* The only state that gets the dash — it means "no value", which is
         exactly what a failed read has. */
      body = "—";
      stateClass = "is-quiet";
    } else if (count == null) {
      body = <span className="at-skeleton" aria-hidden />;
      stateClass = TILE_SKELETON;
    } else if (Number(count) === 0) {
      /*
       * A GREEN ZERO, not a dimmed dash.
       *
       * Zero here is not an absence, it is a result: nothing needs attention.
       * A dash reads as missing information — "we could not get this" — and a
       * dimmed one reads as switched off. Showing the number in the accent
       * says the opposite, and it is still status colour doing status work:
       * green for clear, amber for a queue, red for overdue, neutral for a
       * plain count. Green is reserved for zero and never used for a non-zero
       * value, so it cannot drift into decoration.
       */
      body = "0";
      stateClass = "is-clear";
    } else {
      body = count;
      stateClass = tone ? `is-${tone}` : "";
    }

    /*
     * ONE shape for all four tiles: the count in a fixed-width column, the
     * label beside it on the same baseline.
     *
     * The count's column is why the labels line up across tiles — "2", "51"
     * and the zero dash are different widths, so without a reserved column
     * every label started at a different x and the grid read as four unrelated
     * boxes.
     *
     * `BdrButton variant="plain"`, NOT `BdrRowButton`: that component is a
     * full-width ROW with a built-in chevron and its own internal layout, and
     * forcing it into a compact tile made the clickable tiles render nothing
     * like the non-clickable one. `plain` is chrome-free, so the tile's own
     * CSS owns the layout and both branches look identical.
     */
    const content = (
      <>
        <span className="at-count">{body}</span>
        <span className="at-label">{label}</span>
      </>
    );
    const fullTitle = isError ? `${label} — couldn't load` : ariaLabel;

    if (!onClick || count == null || isError) {
      /* `aria-label` belongs here too — the Overdue tile never has an onClick,
         so this is its ONLY branch, and "80+" is announced as "eighty plus"
         without it. */
      return (
        <div
          key={key}
          className={`at-tile ${stateClass}`}
          title={fullTitle}
          role="img"
          aria-label={fullTitle}
        >
          {content}
        </div>
      );
    }

    return (
      <BdrButton
        key={key}
        variant="plain"
        className={`at-tile at-tile--btn ${stateClass}`}
        onClick={onClick}
        ariaLabel={ariaLabel}
        title={fullTitle}
      >
        {content}
      </BdrButton>
    );
  };

  return (
    /* No visible heading — the tiles label themselves and a line of helper text
       above four labelled numbers was noise. `aria-label` still names the
       region, so it is only the VISUAL heading that is gone. */
    <section className="attention-tiles" aria-label="At a glance">
      <div className="at-row">
        {renderTile({
          key: "overdue",
          /* The task query is filtered by the admin assignee sort, so this is
             "overdue in the list you are looking at" — not a global count like
             its neighbours. Labelled rather than silently inconsistent. */
          /* The filter stays in the title/aria rather than the visible label:
             inline with the count there is room for one word, and "Overdue ·
             Taylor Turner" would ellipsis to nothing useful. */
          label: "Overdue",
          count: overdueCount === 80 ? "80+" : overdueCount,
          tone: "danger",
          ariaLabel: `${
            overdueCount === 80 ? "80 or more" : overdueCount
          } overdue tasks${sortLabel ? ` — ${sortLabel}` : ""}`,
        })}
        {renderTile({
          key: "quotes",
          label: "Quotes",
          count: quotes,
          isError: failed.quotes,
          /* No tone — a volume readout never takes the attention colour. */
          onClick: () => setQuoteModalOpen(true),
          ariaLabel: `${quotes} quote requests in the last 7 days`,
        })}
        {renderTile({
          key: "jobforms",
          label: "Job Forms",
          count: jobForms,
          isError: failed.jobForms,
          tone: "warning",
          onClick: () => navigate("/bdr-office/job-form-review"),
          ariaLabel: `${jobForms} job forms pending review`,
        })}
        {renderTile({
          key: "referrals",
          label: "Referrals",
          count: referrals,
          isError: failed.referrals,
          tone: "warning",
          onClick: () => navigate("/bdr-office/referrals"),
          ariaLabel: `${referrals} open referrals`,
        })}
      </div>

      <QuoteRequestsModal
        open={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
      />
    </section>
  );
};

export default AttentionTiles;
